/**
 * AudioManagerModal - Dashboard panel for managing patient audio files
 * Shows all patient audios with transcripts and available backups.
 * The Backups tab lists objects only from Supabase Storage (`backup-audio`,
 * `transcription-backups`) under prefixes for the patient's visit folder
 * `{schema}/{year}/{date}` — same rule as uploads (patient row date, not UI "today").
 * An in-memory TTL cache may avoid repeat bucket scans in the same session;
 * it does not use localStorage/sessionStorage.
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Button,
  Text,
  Box,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Divider,
  Spinner,
  useToast,
  Flex,
  IconButton,
  Checkbox,
  Input,
  ModalFooter,
  FormLabel,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon, DownloadIcon, SearchIcon } from "@chakra-ui/icons";
import { getSupabase } from "./supabaseClient";
import { tableName, BASE_URLS, prompts, supabaseUrl } from "./env";
import AudioPlayer from "./table/audio/AudioPlayer";
import { pickAudioDisplayName } from "./table/audio/AudioPlayerModal";
import { recordingLabel } from "../utils/recordingLabel";
import { BACKUP_CONFIG } from "../pages/AudioRecorder/utils/transcriptionBackupUtils";
import { getUserSchema, resolveStorageFolderYearDateForPatientId } from "../pages/AudioRecorder/utils/databaseUtils";
import {
  fetchAudioMetadata,
  mergeAudioMetadata,
  isMetadataFile,
  getMetadataFilename,
  backupAudioFilenamePairsTranscriptFile,
  guessTranscriptionJsonPathFromBackupAudioStoragePath,
  deduplicateBySessionId,
  matchTranscriptionToAudio,
  groupBackupsBySessionId,
  generateSessionIdFromFilename,
  transcriptShowsAsLive,
  resolveClinicalAudioSlotFromBackupSidecarAndFilename,
} from "../utils/audioMetadata";

const BACKUP_AUDIO_BUCKET = "backup-audio";

/** Clinical slot shown in Audio Manager; merges transcript JSON fields + `_AudioN.json` filename. */
const resolvedTranscriptBackupClinicalSlot = (t) =>
  !t
    ? null
    : resolveClinicalAudioSlotFromBackupSidecarAndFilename(
        {
          ...(t.metadata || {}),
          audio_number: t.audio_number,
          audio_index: t.audio_index,
        },
        t.file_name,
      );

// In-memory session cache for last bucket scan only (not persisted; not localStorage).
const backupCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache
const AUDIO_MANAGER_DEBUG = false;
/** When true, skip backup bucket listing and transcript JSON GETs (e.g. if storage RLS/paths cause repeated errors in a deployment). */
const DISABLE_AUDIO_MANAGER_BACKUP_LOADING = false;

/** Sidecar JSON may contain blob:/data: URLs captured in the browser — those must not override the real Storage object URL. */
const isEphemeralOrInvalidPublicAudioUrl = (url) => {
  if (url == null || typeof url !== "string") return true;
  const u = url.trim().toLowerCase();
  return (
    u.startsWith("blob:") ||
    u.startsWith("data:") ||
    u.startsWith("file:")
  );
};

/** Per-segment encoding for Supabase public object URLs. */
const buildPublicStorageObjectUrl = (bucket, pathRelative) => {
  const base = String(supabaseUrl || "").replace(/\/$/, "");
  const encoded = String(pathRelative || "")
    .split("/")
    .filter(Boolean)
    .map((s) => encodeURIComponent(s))
    .join("/");
  return `${base}/storage/v1/object/public/${bucket}/${encoded}`;
};

const pickAudioBackupPublicUrl = (embeddedUrl, storageObjectUrl) => {
  if (
    typeof embeddedUrl === "string" &&
    /^https?:\/\//i.test(embeddedUrl) &&
    !isEphemeralOrInvalidPublicAudioUrl(embeddedUrl)
  ) {
    return embeddedUrl.split("?")[0];
  }
  return storageObjectUrl;
};

/** Stable object path within bucket (e.g. schema/2026/date/file.webm). */
const getAudioBackupStoragePath = (backup) => {
  if (backup?.storage_path) return backup.storage_path;
  const metadata = backup?.metadata || {};
  const folder = metadata.folder
    ? String(metadata.folder).replace(/^\/+|\/+$/g, "")
    : "";
  const name = backup?.file_name;
  if (folder && name) return `${folder}/${name}`;
  return name || "";
};

const normalizeAudioUrlForCompare = (url) =>
  typeof url === "string" ? url.trim().split("?")[0] : "";

const isBackupAudioBucketUrl = (url) => {
  const u = normalizeAudioUrlForCompare(url).toLowerCase();
  return (
    u.includes("/object/public/backup-audio/") || u.includes("/backup-audio/")
  );
};

/** HTTPS URL for fetching audio backups (never blob:/data:). */
const resolveAudioBackupFetchUrl = (backup) => {
  if (!backup || backup.backup_type === "transcription") {
    return "";
  }
  const raw = backup.audio_url;
  if (
    typeof raw === "string" &&
    /^https?:\/\//i.test(raw) &&
    !isEphemeralOrInvalidPublicAudioUrl(raw)
  ) {
    return raw.split("?")[0];
  }
  const path = getAudioBackupStoragePath(backup);
  if (!path) return "";
  return buildPublicStorageObjectUrl(BACKUP_AUDIO_BUCKET, path);
};

/** When this backup's recording is already listed under Current Audios. */
const findCurrentAudioMatchingBackup = (backup, currentAudios) => {
  if (!backup || !Array.isArray(currentAudios) || currentAudios.length === 0) {
    return null;
  }
  const canonicalSessionId =
    backup.metadata?.session_id ||
    backup.transcript_backup?.metadata?.session_id ||
    backup.transcript_backup?.session_id ||
    backup.session_id ||
    "";
  const backupUrl = normalizeAudioUrlForCompare(
    resolveAudioBackupFetchUrl(backup),
  );
  const metaLiveUrl = normalizeAudioUrlForCompare(backup.metadata?.audio_url);

  for (const a of currentAudios) {
    const liveUrl = normalizeAudioUrlForCompare(a.url || a.audio_url);
    if (canonicalSessionId && a.session_id === canonicalSessionId) return a;
    if (backupUrl && liveUrl === backupUrl) return a;
    if (
      metaLiveUrl &&
      !isBackupAudioBucketUrl(metaLiveUrl) &&
      liveUrl === metaLiveUrl
    ) {
      return a;
    }
  }
  return null;
};

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

/**
 * Storage prefixes to list for backup-audio and transcription-backups.
 * Uses the same `{schema}/{year}/{YYYY-MM-DD}` visit folder as recordings.
 * @param {string} yyyyMmDd - Resolved visit date from patient row (fallback: caller may pass today's ISO date).
 */
const buildBackupScanFolderPaths = (schema, yyyyMmDd) => {
  const dateStr =
    typeof yyyyMmDd === "string" && /^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd.trim())
      ? yyyyMmDd.trim()
      : toIsoDate(new Date());
  if (!dateStr) return [];
  const year = dateStr.slice(0, 4);
  const sch = String(schema || "public").replace(/^\/+|\/+$/g, "");
  const paths = new Set();
  paths.add(`${sch}/${year}/${dateStr}`);
  paths.add(`${year}/${dateStr}`);
  paths.add(`public/${year}/${dateStr}`);
  return Array.from(paths);
};

const mergedStateVisitDateHint = (merged) =>
  merged?.date_ ?? merged?.scheduledDate ?? merged?.date ?? null;

/**
 * Matches backend derive_postgrest_schema_from_backup_storage_path:
 * `{schema}/{year}/{YYYY-MM-DD}/filename` in backup buckets.
 */
const deriveTenantSchemaFromBackupFilePath = (filePath) => {
  if (!filePath || typeof filePath !== "string") return null;
  const parts = filePath.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (parts.length < 3) return null;
  const [seg0, yr, dtRaw] = parts;
  if (!/^\d{4}$/.test(yr)) return null;
  const dtKey = String(dtRaw).split(/[ T]/)[0].split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dtKey)) return null;
  return seg0 || null;
};

// Clear cache for a specific patient (call after deleting audio)
export const clearBackupCache = (patientId) => {
  if (patientId) {
    backupCache.delete(`backup_${patientId}`);
  }
};

// Clear ALL backup caches
const clearAllBackupCaches = () => {
  backupCache.clear();
};

/**
 * Build a stable 1-based "Audio N" label for an audio row (current OR backup).
 *
 * Rules (smart + sync):
 *  1. Use `created_at` (descending tiebreaker for the same instant) to keep
 *     Current Audio 1 <-> Backup Audio 1 stable across both tabs.
 *  2. If a backup's session_id matches a current audio's session_id (or its
 *     primary url), they share the same number — never "Audio 3" alongside
 *     "Audio 3" for the same recording.
 *  3. Orphan backups (no current match) get the next free slots so the user
 *     sees contiguous Audio 1..N across both tabs.
 *  4. When a row lacks session_id OR created_at, fall back to its position
 *     in its own tab so the labels still say "Audio N" without NaN/undefined.
 *
 * Returns:
 *   {
 *     audioNumber: number|null,            // 1-based slot, or null if no data
 *     label: "Audio 1" | "Audio (2)",      // friendly name ready to render
 *   }
 *
 * Safe to call with [] (returns empty Map + null defaults).
 */
const toMillis = (value) => {
  if (!value) return null;
  const t =
    typeof value === "number"
      ? value
      : (() => {
          const d = new Date(value);
          return Number.isNaN(d.getTime()) ? null : d.getTime();
        })();
  return t != null && Number.isFinite(t) ? t : null;
};

const deriveAudioRowSessionId = (row) => {
  if (!row || typeof row !== "object") return "";
  return (
    row.session_id ||
    row.metadata?.session_id ||
    row.transcript_backup?.metadata?.session_id ||
    row.transcript_backup?.session_id ||
    ""
  );
};

const deriveAudioRowCreatedAt = (row) => {
  if (!row || typeof row !== "object") return null;
  return (
    toMillis(row.created_at) ||
    toMillis(row.uploaded_at) ||
    toMillis(row.recording_date) ||
    toMillis(row.metadata?.created_at) ||
    toMillis(row.transcript_backup?.created_at) ||
    null
  );
};

const deriveAudioRowUrl = (row) => {
  if (!row || typeof row !== "object") return "";
  const raw =
    row.url ||
    row.audio_url ||
    row.metadata?.audio_url ||
    row.transcript_backup?.metadata?.audio_url ||
    "";
  if (typeof raw !== "string") return "";
  const cleaned = raw.split("?")[0];
  return cleaned && /^https?:\/\//i.test(cleaned) ? cleaned : "";
};

/**
 * Build the cross-tab "Audio N" numbering. STABLE + MONOTONIC:
 *  - Existing recordings keep their existing label across modal opens, page
 *    reloads, and across the Current Audios / Backups tabs.
 *  - New recordings always receive the next free slot (max existing + 1)
 *    even when their created_at is older than existing rows. This matches
 *    the "saved = next number" expectation users have when they record a
 *    fresh session while the modal shows Audio 3.
 *  - Persisted per patient in localStorage so reopening the modal doesn't
 *    reshuffle labels. Backed by a process-level fallback (module Map) so
 *    private-mode browsers without localStorage still work.
 *  - When a session has a matching Current audio AND a matching Backup row,
 *    they share one number (one recording = one label).
 *
 * Safe to call with []. localStorage failures are caught silently and the
 * in-process Map is used as a fallback.
 */
const AUDIO_NUMBERING_NAMESPACE = "notesai.audioNumbering.v1";
const inProcessAudioNumberCache = new Map(); // patientId -> { byKey, allocatedTo }

const audioNumberingStorageKey = (patientId) =>
  `${AUDIO_NUMBERING_NAMESPACE}.${patientId || "unknown"}`;

const loadAudioNumberState = (patientId) => {
  const fallback = { byKey: new Map(), allocatedTo: 0 };
  if (!patientId || typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage?.getItem(audioNumberingStorageKey(patientId));
    if (!raw) return inProcessAudioNumberCache.get(patientId) || fallback;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return fallback;
    const entries = Array.isArray(parsed.byKey) ? parsed.byKey : [];
    const byKey = new Map();
    for (const [k, v] of entries) {
      if (typeof k === "string" && Number.isFinite(v) && v >= 1) {
        byKey.set(k, Number(v));
      }
    }
    const allocatedTo =
      typeof parsed.allocatedTo === "number" && parsed.allocatedTo >= 0
        ? parsed.allocatedTo
        : 0;
    const state = { byKey, allocatedTo };
    inProcessAudioNumberCache.set(patientId, state);
    return state;
  } catch (_) {
    return inProcessAudioNumberCache.get(patientId) || fallback;
  }
};

const saveAudioNumberState = (patientId, state) => {
  inProcessAudioNumberCache.set(patientId, state);
  if (!patientId || typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(
      audioNumberingStorageKey(patientId),
      JSON.stringify({
        byKey: Array.from(state.byKey.entries()),
        allocatedTo: state.allocatedTo,
        savedAt: Date.now(),
      }),
    );
  } catch (_) {
    // Ignore quota / private-mode failures; the in-process cache still works.
  }
};

export const resetAudioNumberingForPatient = (patientId) => {
  if (!patientId) return;
  inProcessAudioNumberCache.delete(patientId);
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.removeItem(audioNumberingStorageKey(patientId));
  } catch (_) {
    /* ignore */
  }
};

/**
 * @param {string|null} patientId
 * @param {Array} moreAudios - Current audios (deduplicated by session_id).
 * @param {Array} backupList - Backup rows.
 * @returns {{
 *   byKey: Map<string, number>,
 *   labelFor: (row: any) => string,
 *   numberFor: (row: any) => number|null,
 *   allocatedTo: number,
 *   save: () => void,
 * }}
 */
const buildCrossTabAudioNumbering = (patientId, moreAudios, backupList) => {
  const state = loadAudioNumberState(patientId);
  const { byKey } = state;

  const getRowKey = (row) => {
    const sid = deriveAudioRowSessionId(row);
    if (sid) return `sid::${sid}`;
    const url = deriveAudioRowUrl(row);
    if (url) return `url::${url}`;
    return `idx::${row?.file_name || row?.url || row?.audio_url || ""}`;
  };

  // Stable ordering of inputs for first-time allocation so the first assign
  // of "next free slot" is deterministic across renders/refreshes.
  const inputOrder = [...((Array.isArray(moreAudios) ? moreAudios : []) || [])
    .map((r) => ({ key: getRowKey(r), src: r, kind: "current" })),
    ...(((Array.isArray(backupList) ? backupList : []) || [])
      .filter((b) => b?.backup_type === "audio")
      .map((r) => ({ key: getRowKey(r), src: r, kind: "backup" })))]
    .sort((a, b) => {
      const at = deriveAudioRowCreatedAt(a.src) || 0;
      const bt = deriveAudioRowCreatedAt(b.src) || 0;
      if (at !== bt) return at - bt;
      return a.key.localeCompare(b.key);
    });

  let allocatedTo = state.allocatedTo || 0;
  for (const entry of inputOrder) {
    if (!byKey.has(entry.key)) {
      allocatedTo += 1;
      byKey.set(entry.key, allocatedTo);
    }
  }

  const finalState = { byKey, allocatedTo };
  saveAudioNumberState(patientId, finalState);

  const labelFor = (row) => {
    if (!row || typeof row !== "object") return "Recording";
    const n = numberFor(row);
    return recordingLabel(row, n);
  };
  const numberFor = (row) => {
    if (!row || typeof row !== "object") return null;
    const key = getRowKey(row);
    const n = byKey.get(key);
    return typeof n === "number" ? n : null;
  };

  return {
    byKey,
    labelFor,
    numberFor,
    allocatedTo,
    save: () => saveAudioNumberState(patientId, finalState),
  };
};

// Preload backups cache for a patient (call from PatientPage on initial load)
export const preloadBackupsCache = async (
  patientId,
  patientData = null,
  supabase = null,
) => {
  if (!patientId) return;

  if (DISABLE_AUDIO_MANAGER_BACKUP_LOADING) {
    return;
  }

  const cacheKey = `backup_${patientId}`;

  // Skip if already cached
  const existing = backupCache.get(cacheKey);
  if (existing && Date.now() - existing.timestamp < CACHE_TTL_MS) {
    return; // Already cached
  }

  try {
    // Get supabase client if not provided
    const supabaseClient = supabase || (await getSupabase());

    // Fetch patient data
    const { data: patientRow } = await supabaseClient
      .from(tableName)
      .select("*")
      .eq("id", patientId)
      .maybeSingle();

    const mergedState = {
      ...(patientData || {}),
      ...(patientRow || {}),
    };

    // Collect ALL matching backups (same logic as loadBackups but without state updates)
    const allAudioBackups = [];
    const allTranscriptBackups = [];

    const schema =
      (await getUserSchema()) || mergedState.schema_name || "public";
    const { date: visitFolderDate } =
      await resolveStorageFolderYearDateForPatientId(
        patientId,
        mergedStateVisitDateHint(mergedState),
      );
    const folderArray = buildBackupScanFolderPaths(schema, visitFolderDate);
    const patientName =
      mergedState.patient_name || mergedState[prompts?.[0]?.[0]] || "";

    const sanitizePatientNameForBackup = (name) => {
      if (!name || typeof name !== "string") return "unknown_patient";
      return (
        name
          .trim()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_+|_+$/g, "") || "unknown_patient"
      );
    };

    const sanitizedName = sanitizePatientNameForBackup(patientName);

    const patientIdMatchesBackup = (pid, backupPatientId) => {
      if (!pid || !backupPatientId) return false;
      return pid === backupPatientId;
    };

    const filenameMatchesPatient = (filename) => {
      if (!filename) return false;
      const lowerFilename = filename.toLowerCase();
      const lowerName = sanitizedName.toLowerCase();
      return (
        lowerFilename.includes(lowerName) || lowerName.includes(lowerFilename)
      );
    };

    // Parallel bucket scanning
    const [audioResults, transcriptResults] = await Promise.all([
      (async () => {
        const results = [];
        const bucket = BACKUP_AUDIO_BUCKET;
        const batchSize = 20;
        const seenAudioStoragePaths = new Set();
        for (let i = 0; i < folderArray.length; i += batchSize) {
          const batch = folderArray.slice(i, i + batchSize);
          const batchResults = await Promise.all(
            batch.map(async (folder) => {
              const normalized = folder.replace(/^\/+|\/+$/g, "");
              const { data: files, error } = await supabaseClient.storage
                .from(bucket)
                .list(normalized, {
                  limit: 100,
                  sortBy: { column: "created_at", order: "desc" },
                });
              if (error || !files) return [];
              const folderFileNameSet = new Set(
                files.map((file) => file?.name).filter(Boolean),
              );
              return files
                .filter(
                  (f) =>
                    f.name &&
                    !f.name.startsWith(".") &&
                    f.name !== ".keep" &&
                    !isMetadataFile(f.name) &&
                    !f.name.endsWith(".json") &&
                    filenameMatchesPatient(f.name),
                )
                .map((f) => ({
                  ...f,
                  normalized,
                  folder,
                  folderFileNameSet,
                }));
            }),
          );
          for (const files of batchResults) {
            for (const f of files) {
              const { normalized, folder: fileFolder, folderFileNameSet } = f;
              const storagePath = `${normalized}/${f.name}`;
              if (seenAudioStoragePaths.has(storagePath)) continue;
              seenAudioStoragePaths.add(storagePath);
              const publicUrl = buildPublicStorageObjectUrl(bucket, storagePath);

              // Try to read metadata from JSON sidecar file (metadata-driven approach)
              let embeddedMetadata = null;

              try {
                const metadataFilename = getMetadataFilename(f.name);
                if (folderFileNameSet?.has(metadataFilename)) {
                  const metadataPath = `${normalized}/${metadataFilename}`;
                  // bucket `backup-audio` is PRIVATE — /object/public/ fetch returns 400.
                  // Sign the URL so the sidecar (which carries duration) is reachable.
                  const { data: metaSigned } = await supabaseClient.storage
                    .from(bucket)
                    .createSignedUrl(metadataPath, 60);
                  const metadataUrl =
                    metaSigned?.signedUrl ||
                    buildPublicStorageObjectUrl(bucket, metadataPath);
                  const metadataRes = await fetch(metadataUrl);
                  if (metadataRes.ok) {
                    embeddedMetadata = await metadataRes.json();
                  }
                }
              } catch (metadataError) {
                // Metadata file not found - fallback to filename parsing for backward compatibility
              }

              const audioIndex = resolveClinicalAudioSlotFromBackupSidecarAndFilename(
                embeddedMetadata,
                f.name,
              );
              results.push({
                session_id:
                  embeddedMetadata?.session_id ||
                  `audio_${normalized}_${f.name}`,
                patient_name:
                  embeddedMetadata?.patient_name || patientName || "Unknown",
                audio_index: audioIndex,
                audio_number: audioIndex,
                created_at: embeddedMetadata?.created_at || f.created_at,
                duration: embeddedMetadata?.duration || null,
                audio_url: pickAudioBackupPublicUrl(
                  embeddedMetadata?.audio_url,
                  publicUrl,
                ),
                file_name: f.name,
                storage_path: storagePath,
                metadata: {
                  ...(embeddedMetadata || {}),
                  folder: fileFolder,
                  bucket,
                  session_id: embeddedMetadata?.session_id || null,
                  hasEmbeddedMetadata: embeddedMetadata !== null,
                },
                patient_id: embeddedMetadata?.patient_id || patientId,
                schema_name: embeddedMetadata?.schema_name || schema,
                backup_type: "audio",
              });
            }
          }
        }
        return results;
      })(),
      (async () => {
        const results = [];
        const bucket = BACKUP_CONFIG.BUCKET_NAME;
        const batchSize = 20;
        const seenTranscriptStoragePaths = new Set();
        for (let i = 0; i < folderArray.length; i += batchSize) {
          const batch = folderArray.slice(i, i + batchSize);
          const batchResults = await Promise.all(
            batch.map(async (folder) => {
              const normalized = folder.replace(/^\/+|\/+$/g, "");
              const { data: files, error } = await supabaseClient.storage
                .from(bucket)
                .list(normalized, {
                  limit: 100,
                  sortBy: { column: "created_at", order: "desc" },
                });
              if (error || !files) return [];
              return files
                .filter(
                  (f) =>
                    f.name &&
                    f.name.endsWith(".json") &&
                    !f.name.startsWith(".") &&
                    f.name !== ".keep",
                )
                .map((f) => ({ ...f, normalized }));
            }),
          );
          for (const files of batchResults) {
            for (const f of files) {
              const { normalized } = f;
              const storagePath = `${normalized}/${f.name}`;
              if (seenTranscriptStoragePaths.has(storagePath)) continue;
              seenTranscriptStoragePaths.add(storagePath);
              const publicUrl = buildPublicStorageObjectUrl(bucket, storagePath);
              try {
                // bucket `transcription-backups` is PRIVATE — /object/public/ fetch
                // returns 400. Sign the URL so the transcript JSON is reachable.
                const { data: transcriptSigned } = await supabaseClient.storage
                  .from(bucket)
                  .createSignedUrl(storagePath, 60);
                const transcriptUrl =
                  transcriptSigned?.signedUrl || publicUrl;
                const res = await fetch(transcriptUrl);
                if (!res.ok) continue;
                const data = await res.json();
                if (
                  data &&
                  patientIdMatchesBackup(patientId, data.patient_id)
                ) {
                  const resolvedAn =
                    data.audio_number ??
                    data.metadata?.audio_number ??
                    data.metadata?.audio_index ??
                    data.metadata?.audioNumber ??
                    null;
                  results.push({
                    session_id:
                      data.metadata?.session_id ||
                      data.session_id ||
                      `transcript_${normalized}_${f.name}`,
                    patient_name:
                      patientName ||
                      data.metadata?.patient_name ||
                      "Unknown",
                    audio_index: resolvedAn ?? data.metadata?.audio_index,
                    audio_number: resolvedAn,
                    created_at: data.created_at || f.created_at,
                    duration: data.duration,
                    transcript_text: data.transcript_text || "",
                    metadata: {
                      ...(data.metadata || {}),
                      folder: normalized,
                    },
                    patient_id: patientId,
                    schema_name: data.metadata?.schema_name || schema,
                    bucket: data.metadata?.bucket || BACKUP_CONFIG.BUCKET_NAME,
                    backup_type: "transcription",
                    file_name: f.name,
                    storage_path: storagePath,
                  });
                }
              } catch (e) {}
            }
          }
        }
        return results;
      })(),
    ]);

    allAudioBackups.push(...audioResults);
    allTranscriptBackups.push(...transcriptResults);

    // Group transcription backups by resolved clinical slot (filename + JSON metadata).
    const transcriptsByAudioNumber = new Map();
    for (const t of allTranscriptBackups) {
      const key = resolvedTranscriptBackupClinicalSlot(t);
      if (key !== null && key !== undefined) {
        if (!transcriptsByAudioNumber.has(key)) {
          transcriptsByAudioNumber.set(key, []);
        }
        transcriptsByAudioNumber.get(key).push(t);
      }
    }

    const filenamesMatch = backupAudioFilenamePairsTranscriptFile;

    // Match audio backups with transcription backups
    const foundBackups = [];
    const usedTranscriptIndices = new Set();

    for (const audio of allAudioBackups) {
      const audioNum = audio.audio_number;
      let matchedTranscript = null;

      for (const t of allTranscriptBackups) {
        if (usedTranscriptIndices.has(t.session_id)) continue;
        if (filenamesMatch(audio.file_name, t.file_name)) {
          matchedTranscript = t;
          usedTranscriptIndices.add(t.session_id);
          break;
        }
      }

      if (!matchedTranscript) {
        const preloadAudioSessionId = audio.metadata?.session_id;
        if (preloadAudioSessionId) {
          for (const t of allTranscriptBackups) {
            if (usedTranscriptIndices.has(t.session_id)) continue;
            const tSid = t.metadata?.session_id || t.session_id;
            if (
              tSid &&
              (tSid.includes(preloadAudioSessionId) ||
                preloadAudioSessionId.includes(tSid))
            ) {
              matchedTranscript = t;
              usedTranscriptIndices.add(t.session_id);
              break;
            }
          }
        }
      }

      if (!matchedTranscript) {
        if (audioNum !== null && audioNum !== undefined) {
          if (transcriptsByAudioNumber.has(audioNum)) {
            const candidates = transcriptsByAudioNumber.get(audioNum).filter(
              (t) => !usedTranscriptIndices.has(t.session_id),
            );
            if (candidates.length > 0) {
              candidates.sort(
                (a, b) =>
                  new Date(b.created_at || 0).getTime() -
                  new Date(a.created_at || 0).getTime(),
              );
              matchedTranscript = candidates[0];
              usedTranscriptIndices.add(matchedTranscript.session_id);
            }
          } else {
            const fallbackCandidates = allTranscriptBackups.filter((t) => {
              if (usedTranscriptIndices.has(t.session_id)) return false;
              const tSlot = resolvedTranscriptBackupClinicalSlot(t);
              return (
                tSlot !== null &&
                tSlot !== undefined &&
                Number(tSlot) === Number(audioNum)
              );
            });
            if (fallbackCandidates.length > 0) {
              fallbackCandidates.sort(
                (a, b) =>
                  new Date(b.created_at || 0).getTime() -
                  new Date(a.created_at || 0).getTime(),
              );
              matchedTranscript = fallbackCandidates[0];
              usedTranscriptIndices.add(matchedTranscript.session_id);
            }
          }
        }
      }

      if (matchedTranscript) {
        const clinicalSlot =
          audio.audio_number ??
          audio.audio_index ??
          resolvedTranscriptBackupClinicalSlot(matchedTranscript);
        foundBackups.push({
          ...audio,
          session_id:
            matchedTranscript.metadata?.session_id ||
            matchedTranscript.session_id ||
            audio.session_id ||
            `audio_backup_${foundBackups.length}`,
          audio_index:
            clinicalSlot !== null && clinicalSlot !== undefined
              ? clinicalSlot
              : audio.audio_index,
          audio_number:
            clinicalSlot !== null && clinicalSlot !== undefined
              ? clinicalSlot
              : audio.audio_number,
          transcript_backup: matchedTranscript,
          has_transcript: true,
          metadata: {
            ...(audio.metadata || {}),
            session_id:
              audio.metadata?.session_id ||
              matchedTranscript.metadata?.session_id ||
              matchedTranscript.session_id,
          },
        });
      } else {
        foundBackups.push({
          ...audio,
          session_id: audio.session_id || `audio_backup_${foundBackups.length}`,
          transcript_backup: null,
          has_transcript: false,
        });
      }
    }

    // Transcription JSONs without a paired backup-audio object are intentionally
    // omitted here (recover via Backup Recovery / other flows; not listed standalone).

    foundBackups.sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime(),
    );

    // Store in cache
    backupCache.set(cacheKey, {
      data: foundBackups,
      timestamp: Date.now(),
      validated: false,
    });
  } catch (error) {
    console.warn("preloadBackupsCache failed:", error);
  }
};

const AudioManagerModal = ({
  isOpen,
  onClose,
  patientData, // Contains more_audios and transcripts
  patientId,
  onDeleteAudio,
  /** (audioRow, newFilenameStem) => Promise — renames storage + DB (see backend rename-audio) */
  onRenameAudio,
  onTranscribeAudio,
  isTranscribing = false,
  /** Called after a successful restore so the parent can reload `more_audios` / transcripts from the DB */
  onRestoreFromBackupSuccess,
  readOnly = false,
  /**
   * The patient row's provider schema. Defaults to the viewer's schema, which
   * is right everywhere except SuperAdmin, where the patient can belong to
   * any provider: the row read, the backup folders and the restore target all
   * follow it when given.
   */
  schemaName = null,
  /**
   * Extra `{schema}/{year}/{date}` folders to scan for backups — files a
   * recorder saved under another provider's folder for this patient.
   */
  extraBackupFolders = [],
  /** Render the panel in place (no modal chrome), e.g. inside a list row. */
  inline = false,
  /**
   * (audioRow, label) => void — shows "Find transcript" on audios with no
   * transcript (SuperAdmin: search the DB and backups, then link one).
   */
  onFindTranscript,
}) => {
  const [activeTab, setActiveTab] = useState("current"); // "current" or "backups"
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [cachedKey, setCachedKey] = useState(null);
  const [selectedBackups, setSelectedBackups] = useState(new Set());
  const [deletingBackups, setDeletingBackups] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(null);
  const [restoringBackup, setRestoringBackup] = useState(null);
  const [renameModalAudio, setRenameModalAudio] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renamingBusy, setRenamingBusy] = useState(false);

  // Pagination state for backups
  const [backupPage, setBackupPage] = useState(1);
  const BACKUPS_PER_PAGE = 10;

  const toast = useToast();

  // Get patient audios and transcripts (deduplicated by session_id)
  const moreAudios = deduplicateBySessionId(patientData?.more_audios || []);
  const transcripts = patientData?.transcripts || [];

  // Stable cross-tab "Audio N" labels so a recording shows the same number
  // in both Current Audios and Backups. Persisted per-patient in localStorage
  // so existing labels never reshuffle when new content arrives.
  const audioNumbering = useMemo(
    () => buildCrossTabAudioNumbering(patientId, moreAudios, backupList),
    [patientId, moreAudios, backupList],
  );

  const getBackendBaseUrl = async () => {
    const { BASE_URLS } = await import("./env");
    const hostname = window.location.hostname;
    return hostname === "localhost" || hostname === "127.0.0.1"
      ? BASE_URLS.local.backend
      : BASE_URLS.render.backend;
  };

  const isAudioBackupPlayable = (url) =>
    new Promise((resolve) => {
      const audio = document.createElement("audio");
      const finalize = (result) => {
        audio.removeAttribute("src");
        audio.load();
        resolve(result);
      };
      const timer = setTimeout(() => finalize(false), 8000);
      audio.addEventListener("canplay", () => {
        clearTimeout(timer);
        finalize(true);
      });
      audio.addEventListener("error", () => {
        clearTimeout(timer);
        finalize(false);
      });
      audio.preload = "auto";
      audio.src = url;
    });

  const deleteBackupByUrl = async (targetUrl) => {
    const parsed = targetUrl.match(
      /^https:\/\/[^/]+\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/,
    );
    if (!parsed) return false;

    const bucket = parsed[1];
    const filePath = parsed[2].split("?")[0];
    if (!bucket || !filePath) return false;

    try {
      const baseUrl = await getBackendBaseUrl();
      const response = await fetch(
        `${baseUrl}/api/transcription-backup/delete-file/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bucket,
            file_path: filePath,
            patient_id: patientId,
          }),
        },
      );
      return response.ok;
    } catch (error) {
      console.warn("[AudioManager] Failed to auto-delete corrupt backup:", {
        targetUrl,
        error: error?.message || error,
      });
      return false;
    }
  };

  const pruneCorruptedAudioBackups = async (backups) => {
    const cleaned = [];
    let removedCount = 0;

    for (const backup of backups) {
      const isAudioBackup =
        backup?.backup_type === "audio" &&
        (!!backup?.audio_url ||
          !!backup?.storage_path ||
          !!backup?.file_name);
      if (!isAudioBackup) {
        cleaned.push(backup);
        continue;
      }

      const storagePath = getAudioBackupStoragePath(backup);
      if (!storagePath) {
        cleaned.push(backup);
        continue;
      }

      // The backup-audio bucket is PRIVATE, so a HEAD on the public object
      // URL returns 400 even when the object exists — and the previous
      // implementation interpreted that as corruption and DELETED the
      // file from storage. Sign the URL before probing so private-bucket
      // objects survive the check.
      let fetchUrl = "";
      try {
        const supabase = await getSupabase();
        const { data: signed, error: signErr } = await supabase.storage
          .from(BACKUP_AUDIO_BUCKET)
          .createSignedUrl(storagePath, 60);
        if (signErr || !signed?.signedUrl) {
          // Cannot sign → leave it in the list rather than risk deleting
          // a live object. The list call already proved it exists.
          cleaned.push(backup);
          continue;
        }
        fetchUrl = signed.signedUrl;
      } catch (_) {
        cleaned.push(backup);
        continue;
      }

      let shouldDelete = false;
      try {
        const access = await fetch(fetchUrl, { method: "HEAD" });
        if (!access.ok) {
          shouldDelete = true;
        } else {
          const playable = await isAudioBackupPlayable(fetchUrl);
          shouldDelete = !playable;
        }
      } catch (_) {
        shouldDelete = true;
      }

      if (!shouldDelete) {
        cleaned.push(backup);
        continue;
      }

      const deleted = await deleteBackupByUrl(fetchUrl);
      if (deleted) {
        removedCount += 1;
      } else {
        // Keep only when delete fails, so user can retry manually.
        cleaned.push(backup);
      }
    }

    return { cleaned, removedCount };
  };

  // Load backup list when switching to backups tab
  useEffect(() => {
    if (activeTab === "backups" && isOpen && patientId) {
      loadBackups();
    }
  }, [activeTab, isOpen, patientId]);

  const loadBackups = async (options = {}) => {
    const bypassCache = options.bypassCache === true;
    setLoadingBackups(true);
    try {
      if (DISABLE_AUDIO_MANAGER_BACKUP_LOADING) {
        setBackupList([]);
        setCachedKey(null);
        setLoadingBackups(false);
        return;
      }

      // Reuse last Supabase bucket scan for this patient unless explicitly refreshing.
      const cacheKey = `backup_${patientId}`;
      if (!bypassCache) {
        const cached = backupCache.get(cacheKey);
        if (
          cached &&
          cached.validated === true &&
          Date.now() - cached.timestamp < CACHE_TTL_MS
        ) {
          setBackupList(cached.data);
          setCachedKey(cacheKey);
          setLoadingBackups(false);
          return;
        }
      }

      // Fetch patient data to build merged state for backup search
      const supabase = await getSupabase();
      const rowClient = schemaName ? supabase.schema(schemaName) : supabase;
      const { data: patientRow } = await rowClient
        .from(tableName)
        .select("*")
        .eq("id", patientId)
        .maybeSingle();

      // Merge patient data with any available data from props
      const mergedState = {
        ...(patientData || {}),
        ...(patientRow || {}),
      };

      // Collect ALL matching backups (both audio files and transcription backups)
      const allAudioBackups = [];
      const allTranscriptBackups = [];

      const schema =
        schemaName || (await getUserSchema()) || mergedState.schema_name || "public";
      const { date: visitFolderDate } =
        await resolveStorageFolderYearDateForPatientId(
          patientId,
          mergedStateVisitDateHint(mergedState),
        );
      const folderArray = Array.from(
        new Set([
          ...buildBackupScanFolderPaths(schema, visitFolderDate),
          ...(extraBackupFolders || []).filter(Boolean),
        ]),
      );
      const patientName =
        mergedState.patient_name || mergedState[prompts?.[0]?.[0]] || "";

      // Helper to sanitize patient name for backup matching
      const sanitizePatientNameForBackup = (name) => {
        if (!name || typeof name !== "string") return "unknown_patient";
        return (
          name
            .trim()
            .replace(/[^\w\s-]/g, "")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "") || "unknown_patient"
        );
      };

      const sanitizedName = sanitizePatientNameForBackup(patientName);

      // Helper to check if patient ID matches backup
      const patientIdMatchesBackup = (pid, backupPatientId) => {
        if (!pid || !backupPatientId) return false;
        return pid === backupPatientId;
      };

      // Helper to check if filename contains patient name
      const filenameMatchesPatient = (filename) => {
        if (!filename) return false;
        const lowerFilename = filename.toLowerCase();
        const lowerName = sanitizedName.toLowerCase();
        return (
          lowerFilename.includes(lowerName) || lowerName.includes(lowerFilename)
        );
      };

      // SCAN BOTH BUCKETS IN PARALLEL
      const [audioResults, transcriptResults] = await Promise.all([
        // SCAN BACKUP-AUDIO BUCKET (for audio file backups)
        (async () => {
          const results = [];
          const bucket = BACKUP_AUDIO_BUCKET;
          // Process folders in batches for parallel queries
          const batchSize = 20;
          const seenAudioStoragePaths = new Set();
          for (let i = 0; i < folderArray.length; i += batchSize) {
            const batch = folderArray.slice(i, i + batchSize);
            const batchResults = await Promise.all(
              batch.map(async (folder) => {
                const normalized = folder.replace(/^\/+|\/+$/g, "");
                if (AUDIO_MANAGER_DEBUG) {
                  console.log(
                    "[AudioManager] Listing files in folder:",
                    normalized,
                  );
                }
                const { data: files, error } = await supabase.storage
                  .from(bucket)
                  .list(normalized, {
                    limit: 100,
                    sortBy: { column: "created_at", order: "desc" },
                  });
                if (AUDIO_MANAGER_DEBUG) {
                  console.log(
                    "[AudioManager] Storage list result for",
                    normalized + ":",
                    { error, fileCount: files?.length || 0 },
                  );
                }
                if (error || !files) return [];
                const folderFileNameSet = new Set(
                  files.map((file) => file?.name).filter(Boolean),
                );
                return files
                  .filter(
                    (f) =>
                      f.name &&
                      !f.name.startsWith(".") &&
                      f.name !== ".keep" &&
                      !isMetadataFile(f.name) &&
                      !f.name.endsWith(".json") &&
                      filenameMatchesPatient(f.name),
                  )
                  .map((f) => ({
                    ...f,
                    normalized,
                    folder,
                    folderFileNameSet,
                  }));
              }),
            );
            for (const files of batchResults) {
              for (const f of files) {
                const { normalized, folder: fileFolder, folderFileNameSet } = f;
                const storagePath = `${normalized}/${f.name}`;
                if (seenAudioStoragePaths.has(storagePath)) continue;
                seenAudioStoragePaths.add(storagePath);
                const publicUrl = buildPublicStorageObjectUrl(bucket, storagePath);

                // Try to read metadata from JSON sidecar file (metadata-driven approach)
                let embeddedMetadata = null;

                try {
                  const metadataFilename = getMetadataFilename(f.name);
                  if (folderFileNameSet?.has(metadataFilename)) {
                    const metadataPath = `${normalized}/${metadataFilename}`;
                    const metadataUrl = buildPublicStorageObjectUrl(
                      bucket,
                      metadataPath,
                    );
                    const metadataRes = await fetch(metadataUrl);
                    if (metadataRes.ok) {
                      embeddedMetadata = await metadataRes.json();
                    }
                  }
                } catch (metadataError) {
                  // Metadata file not found - fallback to filename parsing for backward compatibility
                  if (AUDIO_MANAGER_DEBUG) {
                    console.log(
                      "[AudioManager] No metadata file found for",
                      f.name,
                      "- using filename fallback",
                    );
                  }
                }

                const audioIndex = resolveClinicalAudioSlotFromBackupSidecarAndFilename(
                  embeddedMetadata,
                  f.name,
                );

                results.push({
                  session_id:
                    embeddedMetadata?.session_id ||
                    `audio_${normalized}_${f.name}`,
                  patient_name:
                    embeddedMetadata?.patient_name || patientName || "Unknown",
                  audio_index: audioIndex,
                  audio_number: audioIndex, // Keep for backward compatibility
                  created_at: embeddedMetadata?.created_at || f.created_at,
                  duration: embeddedMetadata?.duration || null,
                  audio_url: pickAudioBackupPublicUrl(
                    embeddedMetadata?.audio_url,
                    publicUrl,
                  ),
                  file_name: f.name,
                  storage_path: storagePath,
                  metadata: {
                    ...(embeddedMetadata || {}),
                    folder: fileFolder,
                    bucket,
                    session_id: embeddedMetadata?.session_id || null,
                    hasEmbeddedMetadata: embeddedMetadata !== null,
                  },
                  patient_id: embeddedMetadata?.patient_id || patientId,
                  schema_name: embeddedMetadata?.schema_name || schema,
                  backup_type: "audio",
                });
              }
            }
          }
          return results;
        })(),
        // SCAN TRANSCRIPTION-BACKUPS BUCKET (for transcription JSON backups)
        (async () => {
          const results = [];
          const bucket = BACKUP_CONFIG.BUCKET_NAME;
          if (AUDIO_MANAGER_DEBUG) {
            console.log(
              "[AudioManager] Scanning transcription backups bucket:",
              bucket,
            );
            console.log(
              "[AudioManager] Folder array for transcription scan:",
              folderArray,
            );
          }
          const batchSize = 20;
          const seenTranscriptStoragePaths = new Set();
          for (let i = 0; i < folderArray.length; i += batchSize) {
            const batch = folderArray.slice(i, i + batchSize);
            const batchResults = await Promise.all(
              batch.map(async (folder) => {
                const normalized = folder.replace(/^\/+|\/+$/g, "");
                if (AUDIO_MANAGER_DEBUG) {
                  console.log(
                    "[AudioManager] Listing transcription files in folder:",
                    normalized,
                  );
                }
                const { data: files, error } = await supabase.storage
                  .from(bucket)
                  .list(normalized, {
                    limit: 100,
                    sortBy: { column: "created_at", order: "desc" },
                  });
                if (AUDIO_MANAGER_DEBUG) {
                  console.log(
                    "[AudioManager] Storage list result for",
                    normalized + ":",
                    { error, fileCount: files?.length || 0 },
                  );
                }
                if (error || !files) return [];
                return files
                  .filter(
                    (f) =>
                      f.name &&
                      f.name.endsWith(".json") &&
                      !f.name.startsWith(".") &&
                      f.name !== ".keep",
                  )
                  .map((f) => ({ ...f, normalized }));
              }),
            );
            for (const files of batchResults) {
              for (const f of files) {
                const { normalized } = f;
                const storagePath = `${normalized}/${f.name}`;
                if (seenTranscriptStoragePaths.has(storagePath)) continue;
                seenTranscriptStoragePaths.add(storagePath);
                const publicUrl = buildPublicStorageObjectUrl(bucket, storagePath);
                try {
                  const res = await fetch(publicUrl);
                  if (!res.ok) {
                    console.warn(
                      "[AudioManager] Failed to fetch transcript:",
                      publicUrl,
                      res.status,
                    );
                    continue;
                  }
                  const data = await res.json();
                  if (AUDIO_MANAGER_DEBUG) {
                    console.log("[AudioManager] Transcript JSON fetched:", {
                      file: f.name,
                      patient_id: data?.patient_id,
                      matchesCurrent: patientIdMatchesBackup(
                        patientId,
                        data?.patient_id,
                      ),
                      hasTranscript: !!data?.transcript_text,
                      audioNumber: data?.audio_number,
                    });
                  }
                  // Check patient match - handle potential sanitization differences
                  const idMatches = patientIdMatchesBackup(
                    patientId,
                    data?.patient_id,
                  );
                  // Also try filename-based matching for transcription backups
                  const filenameBase = f.name.replace(/\.json$/i, "");
                  const audioMatch = filenameMatchesPatient(filenameBase);

                  if (data && (idMatches || audioMatch)) {
                    const resolvedAn =
                      data.audio_number ??
                      data.metadata?.audio_number ??
                      data.metadata?.audio_index ??
                      data.metadata?.audioNumber ??
                      null;
                    results.push({
                      session_id:
                        data.metadata?.session_id ||
                        data.session_id ||
                        `transcript_${normalized}_${f.name}`,
                      patient_name:
                        patientName ||
                        data.metadata?.patient_name ||
                        "Unknown",
                      audio_index: resolvedAn ?? data.metadata?.audio_index,
                      audio_number: resolvedAn,
                      created_at: data.created_at || f.created_at,
                      duration: data.duration,
                      transcript_text: data.transcript_text || "",
                      metadata: {
                        ...(data.metadata || {}),
                        folder: normalized,
                      },
                      patient_id: patientId,
                      schema_name: data.metadata?.schema_name || schema,
                      backup_type: "transcription",
                      file_name: f.name,
                      storage_path: storagePath,
                    });
                  }
                } catch (e) {
                  // Skip failed JSON fetches silently
                }
              }
            }
          }
          return results;
        })(),
      ]);

      // Use parallel results
      allAudioBackups.push(...audioResults);
      allTranscriptBackups.push(...transcriptResults);

      // Group transcription backups by resolved clinical slot (filename + JSON metadata).
      const transcriptsByAudioNumber = new Map();
      for (const t of allTranscriptBackups) {
        const key = resolvedTranscriptBackupClinicalSlot(t);
        if (key !== null && key !== undefined) {
          if (!transcriptsByAudioNumber.has(key)) {
            transcriptsByAudioNumber.set(key, []);
          }
          transcriptsByAudioNumber.get(key).push(t);
        }
      }

      const filenamesMatch = backupAudioFilenamePairsTranscriptFile;

      // Match audio backups with transcription backups
      const matchedBackups = [];
      const usedTranscriptIndices = new Set();

      // Log for debugging
      if (AUDIO_MANAGER_DEBUG) {
        console.log("[AudioManager] Matching:", {
          audioCount: allAudioBackups.length,
          transcriptCount: allTranscriptBackups.length,
          audioNumbers: allAudioBackups.map((a) => ({
            file: a.file_name,
            num: a.audio_number,
          })),
          transcriptAudioNumbers: allTranscriptBackups.map((t) => ({
            file: t.file_name,
            num: t.audio_number,
            sessionId: t.session_id,
          })),
        });
      }

      for (const audio of allAudioBackups) {
        const audioNum = audio.audio_number;
        let matchedTranscript = null;

        // 1) Filename pairing first: `Prefix_BackupN_*.webm` ↔ `Prefix_AudioN.json` (and rolling rules).
        // Matching by audio_number alone is ambiguous when several JSONs share the same slot.
        for (const t of allTranscriptBackups) {
          if (usedTranscriptIndices.has(t.session_id)) continue;
          if (filenamesMatch(audio.file_name, t.file_name)) {
            matchedTranscript = t;
            usedTranscriptIndices.add(t.session_id);
            if (AUDIO_MANAGER_DEBUG) {
              console.log(
                "[AudioManager] Matched by filename pairing:",
                audio.file_name,
                "↔",
                t.file_name,
              );
            }
            break;
          }
        }

        // 2) session_id in metadata (weaker than stem pairing; substring match for legacy paths)
        if (!matchedTranscript) {
          const audioSessionId = audio.metadata?.session_id;
          if (audioSessionId) {
            for (const t of allTranscriptBackups) {
              if (usedTranscriptIndices.has(t.session_id)) continue;
              const tSessionId = t.metadata?.session_id || t.session_id;
              if (
                tSessionId &&
                (tSessionId.includes(audioSessionId) ||
                  audioSessionId.includes(tSessionId))
              ) {
                matchedTranscript = t;
                usedTranscriptIndices.add(t.session_id);
                if (AUDIO_MANAGER_DEBUG) {
                  console.log(
                    "[AudioManager] Matched by session_id:",
                    audioSessionId,
                    "with",
                    tSessionId,
                  );
                }
                break;
              }
            }
          }
        }

        // 3) audio_number fallback — prefer newest when multiple transcripts share a slot
        if (!matchedTranscript) {
          if (audioNum !== null && audioNum !== undefined) {
            const candidatesPrimary = transcriptsByAudioNumber.has(audioNum)
              ? transcriptsByAudioNumber.get(audioNum).filter(
                  (t) => !usedTranscriptIndices.has(t.session_id),
                )
              : [];
            const candidates =
              candidatesPrimary.length > 0
                ? candidatesPrimary
                : allTranscriptBackups.filter((t) => {
                    if (usedTranscriptIndices.has(t.session_id)) return false;
                    const tSlot = resolvedTranscriptBackupClinicalSlot(t);
                    return (
                      tSlot !== null &&
                      tSlot !== undefined &&
                      Number(tSlot) === Number(audioNum)
                    );
                  });

            if (candidates.length > 0) {
              candidates.sort(
                (a, b) =>
                  new Date(b.created_at || 0).getTime() -
                  new Date(a.created_at || 0).getTime(),
              );
              matchedTranscript = candidates[0];
              usedTranscriptIndices.add(matchedTranscript.session_id);
              if (AUDIO_MANAGER_DEBUG) {
                console.log(
                  "[AudioManager] Matched by resolved clinical audio slot:",
                  audioNum,
                  matchedTranscript.file_name,
                );
              }
            }
          }
        }

        if (matchedTranscript) {
          const clinicalSlot =
            audio.audio_number ??
            audio.audio_index ??
            resolvedTranscriptBackupClinicalSlot(matchedTranscript);
          matchedBackups.push({
            ...audio,
            session_id:
              matchedTranscript.metadata?.session_id ||
              matchedTranscript.session_id ||
              audio.session_id ||
              `audio_backup_${matchedBackups.length}`,
            audio_index:
              clinicalSlot !== null && clinicalSlot !== undefined
                ? clinicalSlot
                : audio.audio_index,
            audio_number:
              clinicalSlot !== null && clinicalSlot !== undefined
                ? clinicalSlot
                : audio.audio_number,
            transcript_backup: matchedTranscript,
            has_transcript: true,
            metadata: {
              ...(audio.metadata || {}),
              session_id:
                audio.metadata?.session_id ||
                matchedTranscript.metadata?.session_id ||
                matchedTranscript.session_id,
            },
          });
        } else {
          // Audio without transcript
          matchedBackups.push({
            ...audio,
            session_id:
              audio.session_id || `audio_backup_${matchedBackups.length}`,
            transcript_backup: null,
            has_transcript: false,
          });
        }
      }

      // Unmatched transcription JSONs (no paired backup-audio row) are not listed here.

      // Sort by backup timestamp (newest first) and collapse duplicate Storage paths
      const backupListDedupeKey = (b) => {
        if (b.storage_path) {
          const buck =
            b.backup_type === "audio"
              ? BACKUP_AUDIO_BUCKET
              : BACKUP_CONFIG.BUCKET_NAME;
          return `${buck}::${b.storage_path}`;
        }
        if (b.backup_type === "audio" && b.file_name && b.metadata?.folder) {
          const folder = String(b.metadata.folder).replace(/^\/+|\/+$/g, "");
          return `${BACKUP_AUDIO_BUCKET}::${folder}/${b.file_name}`;
        }
        if (b.backup_type === "transcription" && b.file_name) {
          return `${BACKUP_CONFIG.BUCKET_NAME}::${b.file_name}`;
        }
        return `session::${b.session_id}`;
      };
      const seenBackupKeys = new Set();
      const dedupedMatched = [];
      for (const row of matchedBackups) {
        const k = backupListDedupeKey(row);
        if (seenBackupKeys.has(k)) continue;
        seenBackupKeys.add(k);
        dedupedMatched.push(row);
      }

      dedupedMatched.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime(),
      );

      const { cleaned: validatedBackups, removedCount } =
        await pruneCorruptedAudioBackups(dedupedMatched);

      // Store in cache
      backupCache.set(cacheKey, {
        data: validatedBackups,
        timestamp: Date.now(),
        validated: true,
      });
      setCachedKey(cacheKey);
      setBackupList(validatedBackups);

      console.log("[AudioManager] Final matched backups:", {
        total: validatedBackups.length,
        audioBackups: validatedBackups.filter((b) => b.backup_type === "audio")
          .length,
        transcriptOnlyBackups: validatedBackups.filter(
          (b) => b.backup_type === "transcription",
        ).length,
        withTranscripts: validatedBackups.filter((b) => b.has_transcript).length,
        removedCorruptAudioBackups: removedCount,
      });

      if (removedCount > 0) {
        toast({
          title: "Corrupted Backups Removed",
          description: `Removed ${removedCount} corrupted backup audio file${removedCount === 1 ? "" : "s"} before loading results.`,
          status: "warning",
          duration: 3500,
          isClosable: true,
        });
      }

      if (validatedBackups.length === 0) {
        toast({
          title: "No Backups Found",
          description:
            "No backup audios or transcriptions found for this patient in storage.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error loading backups:", error);
      toast({
        title: "Error",
        description: "Failed to load backup list: " + error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingBackups(false);
    }
  };

  // Match transcripts to an audio using session_id (primary) or filename (secondary)
  const getTranscriptsForAudio = (audioFile) => {
    if (!transcripts || !Array.isArray(transcripts)) return [];
    // Use session_id-based matching from audioMetadata utilities
    const matched = matchTranscriptionToAudio(audioFile, transcripts);
    if (matched) return [matched];
    return [];
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return "—";
    }
  };

  const canRenameCurrentAudio = (a) =>
    !!a &&
    typeof a.url === "string" &&
    /^https?:\/\//i.test(a.url.trim()) &&
    /\/storage\/v1\/object\/public\//i.test(a.url);

  const defaultStemForRename = (audioRow) => {
    if (!audioRow) return "";
    const nm = (audioRow.name || audioRow.displayName || "").trim();
    if (nm)
      return nm.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "");
    const raw = (audioRow.url || "").split("?")[0];
    const leaf = raw.split("/").filter(Boolean).pop() || "";
    return (
      leaf.replace(/\.(webm|mp3|wav|m4a|ogg)$/i, "") || "recording"
    );
  };

  const confirmRenameCurrentAudio = async () => {
    const stem = renameDraft.trim();
    if (!renameModalAudio || !stem) {
      toast({
        title: "Name required",
        description: "Enter a file name (without changing the extension).",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!onRenameAudio) return;
    setRenamingBusy(true);
    try {
      await onRenameAudio(renameModalAudio, stem);
      setRenameModalAudio(null);
      setRenameDraft("");
      clearBackupCache(patientId);
    } catch {
      // Parent shows error toast
    } finally {
      setRenamingBusy(false);
    }
  };

  // Get bucket and file path from backup metadata
  const getBackupStorageInfo = (backup) => {
    const isAudioBackup = backup.backup_type === "audio";
    const bucket = isAudioBackup
      ? BACKUP_AUDIO_BUCKET
      : BACKUP_CONFIG.BUCKET_NAME;
    const metadata = backup.metadata || {};
    let filePath = backup.storage_path;
    if (!filePath) {
      const folder = metadata.folder
        ? String(metadata.folder).replace(/^\/+|\/+$/g, "")
        : "";
      const leaf =
        backup.file_name ||
        (typeof backup.session_id === "string"
          ? backup.session_id.split("_").pop()
          : "");
      filePath = folder ? `${folder}/${leaf}` : leaf;
    }
    return { bucket, filePath };
  };

  // Toggle backup selection
  const toggleBackupSelection = (sessionId) => {
    const newSelected = new Set(selectedBackups);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedBackups(newSelected);
  };

  // Toggle select all backups
  const toggleSelectAllBackups = () => {
    if (selectedBackups.size === backupList.length) {
      setSelectedBackups(new Set());
    } else {
      setSelectedBackups(new Set(backupList.map((b) => b.session_id)));
    }
  };

  // Delete selected backups
  const deleteSelectedBackups = async () => {
    if (selectedBackups.size === 0) {
      toast({
        title: "No Backups Selected",
        description: "Please select at least one backup to delete.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedBackups.size} selected backup${selectedBackups.size > 1 ? "s" : ""}? This action cannot be undone.`,
    );

    if (!confirmed) return;

    setDeletingBackups(true);
    let deletedCount = 0;
    let errorCount = 0;

    try {
      const { BASE_URLS } = await import("./env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      for (const sessionId of selectedBackups) {
        const backup = backupList.find((b) => b.session_id === sessionId);
        if (!backup) continue;

        const { bucket, filePath } = getBackupStorageInfo(backup);

        try {
          const response = await fetch(
            `${baseUrl}/api/transcription-backup/delete-file/`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                bucket,
                file_path: filePath,
                patient_id: patientId,
              }),
            },
          );

          if (response.ok) {
            deletedCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          console.error(`Error deleting backup ${sessionId}:`, err);
          errorCount++;
        }
      }

      // Clear selection and refresh
      setSelectedBackups(new Set());

      // Clear cache and reload
      clearBackupCache(patientId);
      await loadBackups();

      toast({
        title: deletedCount > 0 ? "Backups Deleted" : "Deletion Complete",
        description:
          errorCount > 0
            ? `Deleted ${deletedCount} backup${deletedCount !== 1 ? "s" : ""}, ${errorCount} failed.`
            : `Successfully deleted ${deletedCount} backup${deletedCount !== 1 ? "s" : ""}.`,
        status: errorCount > 0 ? "warning" : "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting backups:", error);
      toast({
        title: "Error",
        description: "Failed to delete backups: " + error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingBackups(false);
    }
  };

  // Delete single backup
  const deleteSingleBackup = async (backup) => {
    const confirmed = window.confirm(
      `Delete this backup? This action cannot be undone.`,
    );

    if (!confirmed) return;

    setDeletingBackups(true);

    try {
      const { BASE_URLS } = await import("./env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const { bucket, filePath } = getBackupStorageInfo(backup);

      const response = await fetch(
        `${baseUrl}/api/transcription-backup/delete-file/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bucket,
            file_path: filePath,
            patient_id: patientId,
          }),
        },
      );

      if (response.ok) {
        clearBackupCache(patientId);
        await loadBackups();
        toast({
          title: "Backup Deleted",
          description: "The backup was successfully deleted.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error("Failed to delete backup");
      }
    } catch (error) {
      console.error("Error deleting backup:", error);
      toast({
        title: "Error",
        description: "Failed to delete backup: " + error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingBackups(false);
    }
  };

  // Download single backup file
  const downloadSingleBackup = async (backup) => {
    setDownloadingBackup(backup.session_id);

    try {
      const { bucket, filePath } = getBackupStorageInfo(backup);

      if (backup.backup_type === "audio") {
        const audioFetchUrl = resolveAudioBackupFetchUrl(backup);
        if (!audioFetchUrl) {
          throw new Error("No valid storage URL for this audio backup");
        }
        const response = await fetch(audioFetchUrl);
        if (!response.ok) throw new Error("Failed to fetch audio file");
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", backup.file_name || "backup_audio.webm");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // If this backup has a transcript, offer to download it too
        if (backup.transcript_backup) {
          const transcriptData = {
            patient_id: backup.patient_id,
            patient_name: backup.patient_name,
            transcript_text: backup.transcript_backup.transcript_text,
            duration: backup.transcript_backup.duration,
            created_at: backup.transcript_backup.created_at,
            metadata: backup.transcript_backup.metadata,
          };
          const transcriptBlob = new Blob(
            [JSON.stringify(transcriptData, null, 2)],
            {
              type: "application/json",
            },
          );
          const transcriptUrl = URL.createObjectURL(transcriptBlob);
          const transcriptLink = document.createElement("a");
          transcriptLink.href = transcriptUrl;
          transcriptLink.setAttribute(
            "download",
            `transcript_${backup.file_name || "backup"}.json`,
          );
          document.body.appendChild(transcriptLink);
          transcriptLink.click();
          document.body.removeChild(transcriptLink);
          URL.revokeObjectURL(transcriptUrl);
        }
      } else if (backup.backup_type === "transcription") {
        // For transcription backups, download as JSON file
        const transcriptData = {
          patient_id: backup.patient_id,
          patient_name: backup.patient_name,
          transcript_text: backup.transcript_text,
          duration: backup.duration,
          created_at: backup.created_at,
          metadata: backup.metadata,
        };

        const blob = new Blob([JSON.stringify(transcriptData, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          backup.file_name || `transcript_backup_${backup.session_id}.json`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast({
        title: "Download Started",
        description: "Your backup file is being downloaded.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast({
        title: "Download Failed",
        description: error.message || "Failed to download backup.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDownloadingBackup(null);
    }
  };

  // Restore from backup (for both audio and transcription backups)
  const restoreFromBackup = async (backup) => {
    const isAudioBackup = backup.backup_type === "audio" || backup.audio_url;
    const hasTranscript =
      !!(backup.transcript_backup && backup.has_transcript) ||
      (typeof backup.transcript_text === "string" &&
        backup.transcript_text.trim().length > 0) ||
      (typeof backup.transcript_backup?.transcript_text === "string" &&
        backup.transcript_backup.transcript_text.trim().length > 0);

    if (isAudioBackup) {
      const existingAudio = findCurrentAudioMatchingBackup(backup, moreAudios);
      if (existingAudio) {
        const label =
          pickAudioDisplayName(existingAudio) ||
          existingAudio.displayName ||
          existingAudio.name ||
          existingAudio.filename ||
          "Current audio";
        toast({
          title: "Restore Not Allowed",
          description: `This recording is already in Current Audios as "${label}". You cannot restore this backup.`,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
        return;
      }
    }

    // Build appropriate confirmation message
    let confirmMessage = "";
    if (isAudioBackup && hasTranscript) {
      confirmMessage =
        "Restore both audio and transcript from this backup? This will add them as current audio for this patient.";
    } else if (isAudioBackup) {
      confirmMessage =
        "Restore audio from this backup? This will add it as a current audio for this patient.";
    } else {
      confirmMessage =
        "Restore transcript from this backup? This will update the current transcript for this audio.";
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setRestoringBackup(backup.session_id);

    try {
      const { BASE_URLS } = await import("./env");
      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost" || hostname === "127.0.0.1"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;

      const { bucket, filePath } = getBackupStorageInfo(backup);

      const pathTenantSchema =
        deriveTenantSchemaFromBackupFilePath(filePath) ||
        deriveTenantSchemaFromBackupFilePath(
          backup?.storage_path ? String(backup.storage_path) : "",
        );
      const schemaForRestore =
        pathTenantSchema || backup.schema_name || "public";

      const tbPathRaw = backup.transcript_backup?.storage_path;
      const normalizeStoragePathSegment = (p) =>
        typeof p === "string" ? p.trim().replace(/^\/+|\/+$/g, "") : "";
      /** Same session UUID recorded in backup sidecar / transcription JSON (not synthetic `audio_…` fallback). */
      const canonicalSessionId =
        backup.metadata?.session_id ||
        backup.transcript_backup?.metadata?.session_id ||
        backup.transcript_backup?.session_id ||
        backup.session_id;
      /** Clinical next-audio slot; must match transcription JSON `_Audio{N}` for pairing. */
      const restoreClinicalAudioSlot =
        backup.audio_number ??
        backup.audio_index ??
        backup.metadata?.audio_index ??
        backup.transcript_backup?.audio_number ??
        backup.transcript_backup?.metadata?.audio_index ??
        backup.transcript_backup?.metadata?.audioNumber;
      const transcriptionJsonCandidates = [];
      const pushCandPath = (p) => {
        const n = normalizeStoragePathSegment(p);
        if (n && !transcriptionJsonCandidates.includes(n))
          transcriptionJsonCandidates.push(n);
      };
      pushCandPath(tbPathRaw);
      pushCandPath(
        guessTranscriptionJsonPathFromBackupAudioStoragePath(filePath, {
          clinicalAudioSlot: restoreClinicalAudioSlot,
        }),
      );

      const nameColumn = prompts?.[0]?.[0];
      const patientDisplayForRestore =
        (nameColumn && patientData?.[nameColumn]) ||
        patientData?.patient_name ||
        patientData?.name ||
        "";

      // Prefer text from the transcript row paired with this audio — never "longest string wins"
      // (that could pull the wrong body when multiple fields are populated).
      const tb = backup.transcript_backup;
      let transcriptText = "";
      if (tb && typeof tb === "object") {
        transcriptText = String(
          tb.transcript_text ?? tb.text ?? "",
        ).trim();
      }
      if (!transcriptText) {
        transcriptText = String(backup.transcript_text ?? "").trim();
      }

      const response = await fetch(
        `${baseUrl}/api/transcription-backup/restore/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patient_id: patientId,
            bucket: bucket,
            file_path: filePath,
            session_id: canonicalSessionId,
            audio_number:
              restoreClinicalAudioSlot ?? backup.audio_number ?? undefined,
            schema_name: schemaForRestore,
            // The chart's own schema. Without it the backend writes to the
            // schema in the backup's PATH, which misses the row whenever the
            // recorder filed the backup under another provider's folder.
            ...(schemaName ? { patient_schema_name: schemaName } : {}),
            transcript_text: transcriptText || undefined,
            duration: backup.duration,
            metadata: backup.metadata || {},
            audio_url: resolveAudioBackupFetchUrl(backup) || null,
            is_audio_backup: isAudioBackup,
            patient_display_name: patientDisplayForRestore || undefined,
            ...(transcriptionJsonCandidates.length > 0
              ? {
                  transcription_json_paths: transcriptionJsonCandidates,
                  transcription_json_path: transcriptionJsonCandidates[0],
                  transcription_json_bucket: BACKUP_CONFIG.BUCKET_NAME,
                }
              : {}),
          }),
        },
      );

      if (response.ok) {
        const result = await response.json();
        const restoredData = result.restored_data || {};

        // Determine appropriate success message based on what was actually restored
        let successMessage = "The transcript has been restored from backup.";
        if (isAudioBackup && hasTranscript) {
          if (restoredData.restored_audio && restoredData.restored_transcript) {
            successMessage = "Audio and transcript have been restored from backup.";
          } else if (restoredData.restored_audio) {
            successMessage = "Audio has been restored from backup.";
          } else if (restoredData.restored_transcript) {
            successMessage = "Transcript has been restored from backup. (Audio was a duplicate and skipped)";
          } else {
            successMessage = "Audio already exists. Nothing new was restored.";
          }
        } else if (isAudioBackup) {
          if (
            restoredData.restored_transcript &&
            !restoredData.restored_audio
          ) {
            successMessage =
              "Transcript was restored from backup and linked to the matching audio.";
          } else if (restoredData.restored_audio) {
            successMessage = "Audio has been restored from backup.";
          } else {
            successMessage = "Audio already exists. Restore skipped.";
          }
        }

        toast({
          title: restoredData.restored_audio !== false ? "Restore Successful" : "Duplicate Skipped",
          description: successMessage,
          status: restoredData.restored_audio !== false ? "success" : "info",
          duration: 4000,
          isClosable: true,
        });

        // Log the restore action
        console.log("Restore from backup completed:", {
          session_id: canonicalSessionId,
          is_audio_backup: isAudioBackup,
          has_transcript: hasTranscript,
          restored_data: restoredData,
        });

        // Refresh local backup list cache; reload patient row so "Current Audios" shows restored files
        clearBackupCache(patientId);
        if (typeof onRestoreFromBackupSuccess === "function") {
          try {
            onRestoreFromBackupSuccess();
          } catch (refreshErr) {
            console.warn(
              "[AudioManager] onRestoreFromBackupSuccess:",
              refreshErr,
            );
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const msg =
          errorData.error ||
          (response.status === 409
            ? "This recording already exists in Current Audios. Restore is not allowed."
            : "Failed to restore backup");
        throw new Error(msg);
      }
    } catch (error) {
      console.error("Error restoring backup:", error);
      toast({
        title: "Restore Failed",
        description: error.message || "Failed to restore from backup.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRestoringBackup(null);
    }
  };

  // Reset page when switching tabs or when backup list changes
  useEffect(() => {
    setBackupPage(1);
  }, [activeTab]);

  // Get paginated backups
  const getPaginatedBackups = () => {
    const startIndex = (backupPage - 1) * BACKUPS_PER_PAGE;
    const endIndex = startIndex + BACKUPS_PER_PAGE;
    return backupList.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(backupList.length / BACKUPS_PER_PAGE);

  useEffect(() => {
    const normalizeUrl = (url) => (typeof url === "string" ? url.split("?")[0] : "");

    const handleAudioBackupAutoDeleted = (event) => {
      const deleted = event?.detail || {};
      if (!deleted.audioUrl) return;
      if (deleted.patientId && patientId && deleted.patientId !== patientId) return;

      const deletedUrl = normalizeUrl(deleted.audioUrl);
      const deletedPath = deleted.filePath || "";
      const removedSessionIds = new Set();

      setBackupList((prev) => {
        const next = prev.filter((backup) => {
          const backupUrl = normalizeUrl(resolveAudioBackupFetchUrl(backup));
          if (backupUrl && backupUrl === deletedUrl) {
            if (backup?.session_id) removedSessionIds.add(backup.session_id);
            return false;
          }

          const backupPathFromMetadata = backup?.metadata?.folder
            ? `${backup.metadata.folder}/${backup.file_name || ""}`
            : "";
          if (deletedPath && backupPathFromMetadata && backupPathFromMetadata === deletedPath) {
            if (backup?.session_id) removedSessionIds.add(backup.session_id);
            return false;
          }
          return true;
        });

        if (next.length !== prev.length) {
          clearBackupCache(patientId);
        }
        return next;
      });

      setSelectedBackups((prev) => {
        if (removedSessionIds.size === 0) return prev;
        const next = new Set(prev);
        for (const sessionId of removedSessionIds) next.delete(sessionId);
        return next;
      });
    };

    window.addEventListener("audioBackupAutoDeleted", handleAudioBackupAutoDeleted);
    return () => {
      window.removeEventListener("audioBackupAutoDeleted", handleAudioBackupAutoDeleted);
    };
  }, [patientId]);

  // Render current audio item
  const renderCurrentAudioItem = (audio, index) => {
    const audioTranscripts = getTranscriptsForAudio(audio);
    const hasTranscript = audioTranscripts.length > 0;
    const hasBackup = backupList.some((b) => b.session_id === audio.session_id);
    const friendlyLabel = audioNumbering.labelFor(audio);
    const audioNumberForRow = audioNumbering.numberFor(audio);
    const friendlyDate = deriveAudioRowCreatedAt(audio);

    return (
      <AccordionItem
        key={audio.url || index}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        mb={2}
      >
        <AccordionButton py={2} px={3} _hover={{ bg: "gray.50" }}>
          <HStack flex="1" spacing={2}>
            <Badge colorScheme="blue" variant="solid" fontSize="xs">
              {friendlyLabel}
            </Badge>
            <Badge colorScheme={hasTranscript ? "green" : "gray"}>
              {hasTranscript ? "Transcribed" : "Not Transcribed"}
            </Badge>
            {friendlyDate && (
              <Text fontSize="xs" color="gray.500" title={new Date(friendlyDate).toISOString()}>
                {formatDate(friendlyDate)}
              </Text>
            )}
            {audio.duration && (
              <Text fontSize="xs" color="gray.500">
                {formatDuration(audio.duration)}
              </Text>
            )}
            {audioNumberForRow == null && (
              <Text fontSize="xs" color="gray.400" fontStyle="italic">
                (untracked)
              </Text>
            )}
          </HStack>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={3} px={3}>
          <VStack align="stretch" spacing={3}>
            {/* Audio Player */}
            {audio.url && (
              <Box>
                <AudioPlayer
                  audioUrl={audio.url}
                  audio_duration={audio.duration}
                  patientId={patientId}
                  audioSavedAt={
                    audio.uploaded_at || audio.recording_date || audio.timestamp
                  }
                  audioDisplayName={friendlyLabel}
                />
              </Box>
            )}

            {/* Audio Info */}
            <HStack spacing={4} fontSize="xs" color="gray.600">
              <Text>
                {audio.uploaded_at || audio.recording_date || audio.created_at
                  ? `Recorded: ${formatDate(audio.uploaded_at || audio.recording_date || audio.created_at)}`
                  : "Date: Unknown"}
              </Text>
              {audio.source && <Badge size="sm">{audio.source}</Badge>}
            </HStack>

            {/* Transcripts */}
            {audioTranscripts.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Transcripts ({audioTranscripts.length}):
                </Text>
                {audioTranscripts.map((t) => (
                  <Box
                    key={t.id}
                    fontSize="xs"
                    p={2}
                    bg="blue.50"
                    borderRadius="sm"
                    mb={1}
                  >
                    <HStack justify="space-between">
                      <Text>
                        {transcriptShowsAsLive(t) ? "Live" : "Corti"} -{" "}
                        {formatDate(t.created_at)}
                      </Text>
                      <HStack spacing={1}>
                        {t.facts?.length > 0 && (
                          <Badge size="sm" colorScheme="purple">
                            {t.facts.length} facts
                          </Badge>
                        )}
                        {t.transcript && (
                          <IconButton
                            aria-label="Download transcript"
                            icon={<DownloadIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => {
                              const text = Array.isArray(t.transcript)
                                ? t.transcript
                                    .map((line) =>
                                      typeof line === "string"
                                        ? line
                                        : line?.transcript || line?.text || "",
                                    )
                                    .filter(Boolean)
                                    .join("\n")
                                : String(t.transcript || "");
                              const blob = new Blob([text], { type: "text/plain" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              const dateStr = t.created_at
                                ? new Date(t.created_at).toISOString().split("T")[0]
                                : "transcript";
                              const patientName = (t.name || "").replace(/[^a-zA-Z0-9_-]/g, "_");
                              a.download = `${patientName}_${dateStr}_transcript.txt`;
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                          />
                        )}
                      </HStack>
                    </HStack>
                    {t.transcript && (
                      <Box
                        mt={1}
                        maxH="260px"
                        overflowY="auto"
                        borderRadius="sm"
                        p={1}
                      >
                        <Text
                          color="gray.700"
                          whiteSpace="pre-wrap"
                          wordBreak="break-word"
                          fontSize="xs"
                        >
                          {Array.isArray(t.transcript)
                            ? t.transcript
                                .map((line) =>
                                  typeof line === "string"
                                    ? line
                                    : line?.transcript || line?.text || "",
                                )
                                .filter(Boolean)
                                .join("\n")
                            : t.transcript}
                        </Text>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Actions */}
            <HStack spacing={2} justify="flex-end">
              {!readOnly &&
                onRenameAudio &&
                canRenameCurrentAudio(audio) && (
                  <Button
                    size="sm"
                    colorScheme="gray"
                    variant="outline"
                    leftIcon={<EditIcon />}
                    onClick={() => {
                      setRenameModalAudio(audio);
                      setRenameDraft(defaultStemForRename(audio));
                    }}
                  >
                    Rename…
                  </Button>
                )}
              {!readOnly && !hasTranscript && onFindTranscript && (
                <Button
                  size="sm"
                  colorScheme="orange"
                  variant="outline"
                  leftIcon={<SearchIcon />}
                  onClick={() => onFindTranscript(audio, friendlyLabel)}
                  title="Search the database and transcript backups for this recording's transcript, then link it"
                >
                  Find transcript
                </Button>
              )}
              {!readOnly && !hasTranscript && onTranscribeAudio && (
                <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={() => onTranscribeAudio(audio)}
                  isLoading={isTranscribing}
                >
                  Transcribe
                </Button>
              )}
              {!readOnly && onDeleteAudio && (
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<DeleteIcon />}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete "${audio.name || friendlyLabel}" and its transcription backups?`,
                        )
                      ) {
                        onDeleteAudio(audio);
                      }
                    }}
                >
                  Delete
                </Button>
              )}
            </HStack>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    );
  };

  // Render backup audio item
  const renderBackupAudioItem = (backup, index) => {
    const isAudioBackup = backup.backup_type === "audio";
    const transcriptSide = backup.transcript_backup;
    const friendlyLabel = audioNumbering.labelFor(backup);
    const audioNumberForBackup = audioNumbering.numberFor(backup);
    const isMirroringCurrent =
      isAudioBackup &&
      audioNumberForBackup != null &&
      moreAudios.some(
        (a) =>
          (a?.session_id &&
            (a.session_id === backup.session_id ||
              a.session_id === backup.metadata?.session_id)) ||
          (a?.url &&
            typeof backup.audio_url === "string" &&
            a.url.split("?")[0] === backup.audio_url.split("?")[0]),
      );
    const isOrphan =
      isAudioBackup &&
      audioNumberForBackup != null &&
      audioNumberForBackup > moreAudios.length;
    const friendlyDate = deriveAudioRowCreatedAt(backup);
    const transcriptMeta = transcriptSide?.metadata || {};
    const hasInlineTranscript =
      typeof backup.transcript_text === "string" &&
      backup.transcript_text.trim().length > 0;
    const hasLinkedTranscriptText =
      typeof transcriptSide?.transcript_text === "string" &&
      transcriptSide.transcript_text.trim().length > 0;
    const hasTranscriptJsonLink = !!transcriptSide?.storage_path;
    const hasTranscript =
      !!backup.has_transcript ||
      hasInlineTranscript ||
      hasLinkedTranscriptText ||
      hasTranscriptJsonLink;
    const isSelected = selectedBackups.has(backup.session_id);

    const visitFolderTriple =
      backup.storage_path?.split("/").filter(Boolean).slice(0, 3).join("/") ||
      (typeof backup.metadata?.folder === "string"
        ? String(backup.metadata.folder).replace(/^\/+|\/+$/g, "")
        : "") ||
      "";

    const sidecarCreated =
      backup.metadata?.created_at || backup.metadata?.uploadedAt || null;
    const storageListCreated = backup.created_at || null;
    const transcriptFileCreated =
      transcriptSide?.created_at ||
      transcriptMeta.date ||
      transcriptMeta.timestamp ||
      null;

    const backupRowKey = [
      backup.session_id || "no-session",
      backup.file_name || "no-file",
      backup.created_at || "no-created-at",
      index,
    ].join("|");

    return (
      <AccordionItem
        key={backupRowKey}
        border="1px solid"
        borderColor={isSelected ? "blue.300" : "gray.200"}
        borderRadius="md"
        mb={2}
        bg={isSelected ? "blue.50" : "white"}
      >
        <AccordionButton py={2} px={3} _hover={{ bg: "gray.50" }}>
          <HStack flex="1" spacing={2}>
            {!readOnly && (
              <Checkbox
                isChecked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleBackupSelection(backup.session_id);
                }}
                onClick={(e) => e.stopPropagation()}
                colorScheme="blue"
              />
            )}
            {isAudioBackup ? (
              <>
                <Badge colorScheme="blue" variant="solid" fontSize="xs">
                  {friendlyLabel}
                </Badge>
                {isMirroringCurrent && (
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    Current audio
                  </Badge>
                )}
                {isOrphan && (
                  <Badge colorScheme="orange" variant="subtle" fontSize="xs">
                    Recovery only
                  </Badge>
                )}
                {hasTranscript && (
                  <Badge colorScheme="purple" ml={1}>
                    + Transcript
                  </Badge>
                )}
              </>
            ) : (
              <Badge colorScheme="purple" variant="solid" fontSize="xs">
                {friendlyLabel} (transcript)
              </Badge>
            )}
            {friendlyDate && (
              <Text
                fontSize="xs"
                color="gray.500"
                title={new Date(friendlyDate).toISOString()}
              >
                {formatDate(friendlyDate)}
              </Text>
            )}
            {isAudioBackup && backup.duration != null &&
              Number(backup.duration) > 0 && (
                <Text fontSize="xs" color="gray.500">
                  {formatDuration(backup.duration)}
                </Text>
              )}
            {backup.metadata?.hasEmbeddedMetadata && (
              <Text as="span" fontSize="xs" color="green.500">
                ✓ metadata
              </Text>
            )}
            {backup.file_name && (
              <Text
                fontSize="xs"
                color="gray.400"
                title={backup.file_name}
                maxW="240px"
                noOfLines={1}
              >
                {backup.file_name}
              </Text>
            )}
          </HStack>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={3} px={3}>
          <VStack align="stretch" spacing={3}>
            {/* Backup Info */}
            <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap">
              {visitFolderTriple && (
                <Text fontWeight="medium" color="gray.700">
                  Folder: {visitFolderTriple}
                </Text>
              )}
              <Text>
                Bucket:{" "}
                {backup.metadata?.bucket ||
                  (isAudioBackup
                    ? BACKUP_AUDIO_BUCKET
                    : BACKUP_CONFIG.BUCKET_NAME)}
              </Text>
              {backup.storage_path && (
                <Text maxW="100%" wordBreak="break-all" title={backup.storage_path}>
                  Storage: {backup.storage_path}
                </Text>
              )}
              {sidecarCreated && (
                <Text>Sidecar / backup JSON time: {formatDate(sidecarCreated)}</Text>
              )}
              {storageListCreated &&
                String(storageListCreated) !== String(sidecarCreated) && (
                  <Text>List time: {formatDate(storageListCreated)}</Text>
                )}
              {transcriptFileCreated && (
                <Text>Transcript file time: {formatDate(transcriptFileCreated)}</Text>
              )}
              {backup.metadata?.scheduledDate && (
                <Text>
                  Scheduled (metadata):{" "}
                  {String(backup.metadata.scheduledDate)}
                </Text>
              )}
              {transcriptMeta.ehr_id && (
                <Text>EHR (transcript meta): {transcriptMeta.ehr_id}</Text>
              )}
              {backup.metadata?.ehr_id && (
                <Text>EHR (sidecar): {backup.metadata.ehr_id}</Text>
              )}
              {backup.duration != null &&
                backup.duration !== undefined &&
                Number(backup.duration) > 0 && (
                  <Text>Duration: {formatDuration(backup.duration)}</Text>
                )}
              {transcriptSide?.duration != null &&
                Number(transcriptSide.duration) > 0 && (
                  <Text>
                    Transcript duration: {formatDuration(transcriptSide.duration)}
                  </Text>
                )}
            </HStack>

            {/* Audio Player for audio backups */}
            {isAudioBackup && resolveAudioBackupFetchUrl(backup) && (
              <Box>
                <AudioPlayer
                  audioUrl={resolveAudioBackupFetchUrl(backup)}
                  audio_duration={backup.duration || undefined}
                  patientId={patientId}
                  audioDisplayName={friendlyLabel}
                />
              </Box>
            )}

            {/* Transcript Preview - show from either transcript_backup or direct transcript_text */}
            {(backup.transcript_text ||
              (backup.transcript_backup &&
                backup.transcript_backup.transcript_text)) && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>
                  Transcript Preview:
                </Text>
                <Box
                  fontSize="xs"
                  p={2}
                  bg="purple.50"
                  borderRadius="sm"
                  maxH="100px"
                  overflowY="auto"
                >
                  {(() => {
                    const text =
                      backup.transcript_text ||
                      backup.transcript_backup?.transcript_text ||
                      "";
                    return text.length > 500 ? `${text.slice(0, 500)}…` : text;
                  })()}
                </Box>
              </Box>
            )}

            {backup.metadata && (
              <Box fontSize="xs" color="gray.500">
                {(backup.schema_name || transcriptMeta.schema_name) && (
                  <Text>
                    Schema: {backup.schema_name || transcriptMeta.schema_name}
                  </Text>
                )}
                {backup.patient_id && (
                  <Text>Patient ID: {backup.patient_id}</Text>
                )}
                {(backup.metadata?.session_id || backup.session_id) && (
                  <Text>
                    Session:{" "}
                    {backup.metadata?.session_id || backup.session_id}
                  </Text>
                )}
                {transcriptSide?.storage_path && (
                  <Text wordBreak="break-all">
                    Transcript JSON path: {transcriptSide.storage_path}
                  </Text>
                )}
                {transcriptMeta.session_id &&
                  transcriptMeta.session_id !==
                    (backup.metadata?.session_id || backup.session_id) && (
                    <Text wordBreak="break-all">
                      Transcript session (file): {transcriptMeta.session_id}
                    </Text>
                  )}
              </Box>
            )}

            {/* Actions */}
            <HStack spacing={2} justify="flex-end">
              {!readOnly && (
                <Button
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  onClick={() => restoreFromBackup(backup)}
                  isLoading={restoringBackup === backup.session_id}
                >
                  Restore
                </Button>
              )}
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                leftIcon={<DownloadIcon />}
                onClick={() => downloadSingleBackup(backup)}
                isLoading={downloadingBackup === backup.session_id}
              >
                Download
              </Button>
              {!readOnly && (
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<DeleteIcon />}
                  onClick={() => deleteSingleBackup(backup)}
                  isLoading={deletingBackups}
                >
                  Delete
                </Button>
              )}
            </HStack>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    );
  };

  const headerContent = (
          <HStack spacing={2} flexWrap="wrap">
            <Text>Audio Manager</Text>
            <Badge colorScheme="blue" fontSize="sm">
              {moreAudios.length} Audio{moreAudios.length !== 1 ? "s" : ""}
            </Badge>
            {backupList.length > 0 && (
              <Badge colorScheme="orange" fontSize="sm">
                {backupList.length} Backup{backupList.length !== 1 ? "s" : ""}
              </Badge>
            )}
            <Badge
              colorScheme="purple"
              variant="subtle"
              fontSize="xs"
              title="Audio numbers are stable. New recordings always get the next available slot."
            >
              Numbered 1–{Math.max(
                audioNumbering.allocatedTo || 0,
                moreAudios.length,
              )}
            </Badge>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                if (
                  window.confirm(
                    "Reset Audio numbering for this patient? Next open will renumber from Audio 1.",
                  )
                ) {
                  resetAudioNumberingForPatient(patientId);
                  window.location.reload();
                }
              }}
            >
              Reset numbering
            </Button>
          </HStack>
  );

  const bodyContent = (
        <>
          {/* Tabs */}
          <HStack
            spacing={4}
            mb={4}
            borderBottom="1px solid"
            borderColor="gray.200"
            pb={2}
          >
            <Button
              variant={activeTab === "current" ? "solid" : "ghost"}
              colorScheme={activeTab === "current" ? "blue" : "gray"}
              size="sm"
              onClick={() => setActiveTab("current")}
            >
              Current Audios ({moreAudios.length})
            </Button>
            <Button
              variant={activeTab === "backups" ? "solid" : "ghost"}
              colorScheme={activeTab === "backups" ? "orange" : "gray"}
              size="sm"
              onClick={() => setActiveTab("backups")}
            >
              Backups ({backupList.length})
            </Button>
          </HStack>

          {/* Current Audios Tab */}
          {activeTab === "current" && (
            <VStack align="stretch" spacing={4}>
              {moreAudios.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">No audio files for this patient.</Text>
                </Box>
              ) : (
                <Accordion allowMultiple defaultIndex={[0]}>
                  {moreAudios.map((audio, index) =>
                    renderCurrentAudioItem(audio, index),
                  )}
                </Accordion>
              )}
            </VStack>
          )}

          {/* Backups Tab */}
          {activeTab === "backups" && (
            <VStack align="stretch" spacing={4}>
              {loadingBackups ? (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="orange.500" />
                  <Text ml={3}>Loading backups...</Text>
                </Flex>
              ) : backupList.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Text color="gray.500">
                    No backups found for this patient.
                  </Text>
                  <Button
                    size="sm"
                    mt={2}
                    onClick={() => loadBackups({ bypassCache: true })}
                  >
                    Refresh
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Multi-select header */}
                  <HStack
                    justify="space-between"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                  >
                    <Checkbox
                      isChecked={
                        selectedBackups.size === backupList.length &&
                        backupList.length > 0
                      }
                      isIndeterminate={
                        selectedBackups.size > 0 &&
                        selectedBackups.size < backupList.length
                      }
                      onChange={toggleSelectAllBackups}
                      colorScheme="blue"
                    >
                      <Text fontSize="sm">
                        {selectedBackups.size > 0
                          ? `${selectedBackups.size} selected`
                          : "Select All"}
                      </Text>
                    </Checkbox>
                    {!readOnly && selectedBackups.size > 0 && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        leftIcon={<DeleteIcon />}
                        onClick={deleteSelectedBackups}
                        isLoading={deletingBackups}
                      >
                        Delete Selected ({selectedBackups.size})
                      </Button>
                    )}
                    <HStack spacing={1}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setBackupPage((p) => Math.max(1, p - 1))}
                        isDisabled={backupPage === 1}
                      >
                        Prev
                      </Button>
                      <Text fontSize="sm" px={2}>
                        Page {backupPage} of {totalPages}
                      </Text>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setBackupPage((p) => Math.min(totalPages, p + 1))
                        }
                        isDisabled={backupPage >= totalPages}
                      >
                        Next
                      </Button>
                    </HStack>
                  </HStack>
                  <Accordion allowMultiple>
                    {getPaginatedBackups().map((backup, index) =>
                      renderBackupAudioItem(backup, index),
                    )}
                  </Accordion>
                  {totalPages > 1 && (
                    <HStack justify="center" pt={2}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setBackupPage(1)}
                        isDisabled={backupPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setBackupPage((p) => Math.max(1, p - 1))}
                        isDisabled={backupPage === 1}
                      >
                        Prev
                      </Button>
                      <Text fontSize="sm">
                        Showing {(backupPage - 1) * BACKUPS_PER_PAGE + 1} -{" "}
                        {Math.min(
                          backupPage * BACKUPS_PER_PAGE,
                          backupList.length,
                        )}{" "}
                        of {backupList.length}
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setBackupPage((p) => Math.min(totalPages, p + 1))
                        }
                        isDisabled={backupPage >= totalPages}
                      >
                        Next
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setBackupPage(totalPages)}
                        isDisabled={backupPage >= totalPages}
                      >
                        Last
                      </Button>
                    </HStack>
                  )}
                </>
              )}
            </VStack>
          )}
        </>
  );

  return (
    <>
    {inline ? (
      isOpen && (
        <Box>
          <Box mb={3} fontWeight="semibold">{headerContent}</Box>
          {bodyContent}
        </Box>
      )
    ) : (
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxHeight="85vh">
          <ModalHeader>{headerContent}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>{bodyContent}</ModalBody>
        </ModalContent>
      </Modal>
    )}

    <Modal
      isOpen={!!renameModalAudio}
      onClose={() => {
        if (!renamingBusy) {
          setRenameModalAudio(null);
          setRenameDraft("");
        }
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Rename audio file</ModalHeader>
        <ModalCloseButton isDisabled={renamingBusy} />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={2}>
            Updates storage, the patient record, transcription links, and backup
            metadata when present. The original extension is kept automatically.
          </Text>
          <FormLabel fontSize="sm">New base name</FormLabel>
          <Input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            placeholder="e.g. Visit_1_clinical_note"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && !renamingBusy) {
                e.preventDefault();
                confirmRenameCurrentAudio();
              }
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={() => {
              if (!renamingBusy) {
                setRenameModalAudio(null);
                setRenameDraft("");
              }
            }}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={confirmRenameCurrentAudio}
            isLoading={renamingBusy}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
};

export default AudioManagerModal;
